import { ThemeProvider, createTheme, styled, useTheme } from '@mui/material';
import { type MovieStatus, type ScreenType, type TvStatus } from '@tmdb-graphql-api/schema/types';
import { useMemo } from 'react';
import { BackdropImage } from '#components/BackdropImage/index.tsx';
import { Query as CastAndCrewHighlights } from '#components/CastAndCrew/Highlights/index.ts';
import { DetailsHeader } from '#components/Details/DetailsHeader/index.tsx';
import { DetailsOverview } from '#components/Details/DetailsOverview/index.tsx';
import { DetailsQuery } from '#components/Details/index.ts';
import { themeOverrides } from '#components/SlideoutDrawer/themeOverrides.ts';
import { mapScreenTypeToScreenFormatType, mapScreenTypeToSectionType } from '#helpers/enumMappers.ts';
import { Divider } from '#styled.ts';
import { type ActivePreview, type ActiveVideo } from '#types.ts';

export type TrendingTabPanelProps = {
  id: string | number;
  setActivePreview?: (item: ActivePreview) => void;
  setActiveVideo?: (item: ActiveVideo) => void;
  src: string;
  status?: MovieStatus | TvStatus;
  title: string;
  type: ScreenType;
  voteAverage?: number;
};

const ImageContainer = styled('div')(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  position: 'relative',
  width: '100%',
}));

const ContentContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'rgba(33, 33, 33, .9)',
  borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
  borderTopRightRadius: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  left: 0,
  padding: theme.spacing(3),
  position: 'absolute',
  top: 0,
  width: '34%',
  [theme.breakpoints.between('md', 'lg')]: {
    width: '50%',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    width: '67%',
  },
}));

const ScrollContainer = styled('div')(() => ({
  flexGrow: 1,
  overflowY: 'scroll',
}));

export const TrendingTabPanel = ({
  id,
  setActivePreview,
  setActiveVideo,
  src,
  status,
  title,
  type,
}: TrendingTabPanelProps) => {
  const theme = useTheme();

  const memoDetailsQuery = useMemo(
    () => (
      <>
        <DetailsQuery
          Component={props => (
            <>
              <DetailsHeader
                {...props}
                compact
                linkTitle
                titleLinkOnClick={e => {
                  e.preventDefault();
                  setActivePreview?.({ id, type: mapScreenTypeToSectionType(type) });
                }}
              />
              <Divider />
              <ScrollContainer>
                <DetailsOverview {...props} disableReadMore linesToClamp={3} sx={{ marginBottom: theme.spacing(2) }} />
                <CastAndCrewHighlights id={id} type={mapScreenTypeToScreenFormatType(type)} />
              </ScrollContainer>
            </>
          )}
          componentName="TrendingTabPanel"
          id={id}
          queryScope="brief"
          setActivePreview={setActivePreview}
          type={mapScreenTypeToSectionType(type)}
        />
      </>
    ),
    // We only want to re-execute when id and type change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, type],
  );

  return (
    <ImageContainer>
      <BackdropImage
        id={id}
        pictureContainerSx={{
          [theme.breakpoints.between('md', 'lg')]: {
            paddingBottom: '50%',
          },
          [theme.breakpoints.down('md')]: {
            paddingBottom: '56%',
          },
          paddingBottom: '45%',
        }}
        ribbonSx={{
          left: 'initial',
          right: '-4px',
          transform: 'rotate(90deg)',
        }}
        setActivePreview={setActivePreview}
        setActiveVideo={setActiveVideo}
        showRibbon
        src={src}
        status={status}
        sx={{ border: '1px solid', borderColor: theme.palette.grey[900], borderRadius: theme.spacing(1) }}
        title={title}
        type={mapScreenTypeToSectionType(type)}
        videoButtonSx={{
          [theme.breakpoints.down('md')]: {
            bottom: theme.spacing(1),
            right: theme.spacing(1),
          },
          [theme.breakpoints.between('md', 'lg')]: {
            bottom: theme.spacing(2),
            right: theme.spacing(2),
          },
          bottom: theme.spacing(3),
          left: 'initial',
          right: theme.spacing(3),
          top: 'initial',
        }}
      />
      <ContentContainer>
        <ThemeProvider theme={createTheme({ ...theme, ...themeOverrides })}>{memoDetailsQuery}</ThemeProvider>
      </ContentContainer>
    </ImageContainer>
  );
};
